import React, { useEffect, useMemo, useState } from 'react';
import {
  VStack, Text, Box, HStack, Icon, SimpleGrid, Stat, StatLabel,
  StatNumber, StatHelpText, Flex, Badge, Spinner, Button, Center,
  Input, Wrap, WrapItem, Table, Thead, Tbody, Tr, Th, Td,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
} from '@chakra-ui/react';
import {
  FiCalendar, FiBookOpen, FiUsers, FiMusic, FiTrendingUp, FiRefreshCw,
  FiDownload, FiLayers, FiGrid, FiUser, FiFilter, FiX,
} from 'react-icons/fi';
import { IconType } from 'react-icons';
import axiosInstance from '../../../api/AxiosInstance';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, ArcElement,
  Title, Tooltip, Legend,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import * as XLSX from 'xlsx';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

const RED = '#5b182b';
const RED_DARK = '#691f30';
const COLORS = ['#5b182b', '#C0392B', '#E74C3C', '#F1948A', '#922B21', '#641E16'];

// ─────────── Rango de fecha (filtro client-side) ───────────
const enRango = (v: string | null | undefined, desde: string, hasta: string): boolean => {
  if (!desde && !hasta) return true;
  if (!v) return false;
  const t = new Date(v).getTime();
  if (isNaN(t)) return false;
  if (desde && t < new Date(`${desde}T00:00:00`).getTime()) return false;
  if (hasta && t > new Date(`${hasta}T23:59:59`).getTime()) return false;
  return true;
};

const fmtFecha = (f?: string | null) =>
  f ? new Date(f).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const fmtPesos = (v: any) =>
  v != null && v !== '' ? `$${Number(v).toLocaleString('es-MX', { minimumFractionDigits: 2 })}` : '—';

// ─────────── Tarjeta KPI interactiva (drill-down) ───────────
const TarjetaKPI: React.FC<{
  icon: IconType;
  label: string;
  value: React.ReactNode;
  help: string;
  color: string;
  onClick: () => void;
}> = ({ icon, label, value, help, color, onClick }) => (
  <Box
    bg="white"
    borderRadius="lg"
    boxShadow="sm"
    p={4}
    borderLeft={`4px solid ${color}`}
    cursor="pointer"
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
    transition="all 0.15s ease"
    _hover={{ boxShadow: 'lg', transform: 'translateY(-3px)' }}
    _active={{ transform: 'translateY(-1px)' }}
  >
    <Flex align="center" justify="space-between">
      <Stat>
        <StatLabel fontSize="xs" color="gray.500">{label}</StatLabel>
        <StatNumber fontSize="2xl" color={color}>{value}</StatNumber>
        <StatHelpText fontSize="xs" mb={0}>{help}</StatHelpText>
      </Stat>
      <Icon as={icon} boxSize={8} color={color} opacity={0.4} />
    </Flex>
  </Box>
);

type ModalKey = 'eventos' | 'talleres' | 'inscripciones' | 'planes' | 'programas' | 'promotores' | 'artistas';

const DashboardCultura: React.FC = () => {
  const [cargando, setCargando] = useState(true);

  // Datos crudos (se cargan una sola vez; el filtro de fecha es client-side)
  const [eventos, setEventos] = useState<any[]>([]);
  const [talleres, setTalleres] = useState<any[]>([]);
  const [planes, setPlanes] = useState<any[]>([]);
  const [programas, setProgramas] = useState<any[]>([]);
  const [promotores, setPromotores] = useState<any[]>([]);
  const [artistas, setArtistas] = useState<any[]>([]);

  // Filtro de fecha: valores en edición vs. aplicados (vacío = todos los registros)
  const [desdeInput, setDesdeInput] = useState('');
  const [hastaInput, setHastaInput] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');

  const [modal, setModal] = useState<ModalKey | null>(null);

  const cargar = async () => {
    setCargando(true);
    try {
      const [ev, tal, pl, prom, art] = await Promise.all([
        axiosInstance.get('/cultura/eventos').then((r) => r.data || []).catch(() => []),
        axiosInstance.get('/cultura/talleres').then((r) => r.data || []).catch(() => []),
        axiosInstance.get('/cultura/plan-anual').then((r) => r.data || []).catch(() => []),
        axiosInstance.get('/cultura/promotores').then((r) => r.data || []).catch(() => []),
        axiosInstance.get('/cultura/artistas').then((r) => r.data || []).catch(() => []),
      ]);
      setEventos(ev);
      setTalleres(tal);
      setPlanes(pl);
      setPromotores(prom);
      setArtistas(art);

      // Los programas culturales sólo se listan por plan: se agregan de todos los planes.
      const listasProg = await Promise.all(
        (pl as any[]).map((p) =>
          axiosInstance.get(`/cultura/programas/plan/${p.id}`)
            .then((r) => (r.data || []).map((pg: any) => ({ ...pg, _plan: `${p.anio} — ${p.nombrePlan}` })))
            .catch(() => [] as any[]),
        ),
      );
      setProgramas(listasProg.flat());
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const aplicar = () => { setDesde(desdeInput); setHasta(hastaInput); };
  const limpiar = () => { setDesdeInput(''); setHastaInput(''); setDesde(''); setHasta(''); };
  const hayFiltro = Boolean(desde || hasta);

  // ─────────── Cálculos derivados (recalculan al aplicar el rango) ───────────
  // Eventos → campo de fecha: fechaEvento
  const eventosFilt = useMemo(
    () => eventos.filter((e) => enRango(e.fechaEvento, desde, hasta)),
    [eventos, desde, hasta],
  );
  const eventosActivos = useMemo(() => eventosFilt.filter((e) => e.estatus === 'Activo').length, [eventosFilt]);
  const eventosCerrados = useMemo(() => eventosFilt.filter((e) => e.estatus === 'Cerrado').length, [eventosFilt]);

  // Talleres → campo de fecha: fechaTaller
  const talleresFilt = useMemo(
    () => talleres.filter((t) => enRango(t.fechaTaller, desde, hasta)),
    [talleres, desde, hasta],
  );
  const talleresAbiertos = useMemo(() => talleresFilt.filter((t) => t.estatus === 'Abierto').length, [talleresFilt]);

  // Inscripciones: suma de inscritos de los talleres del periodo (los talleres traen _count.inscripciones)
  const talleresConInscritos = useMemo(
    () => talleresFilt.filter((t) => (t._count?.inscripciones ?? 0) > 0),
    [talleresFilt],
  );
  const inscripcionesTotal = useMemo(
    () => talleresFilt.reduce((s, t) => s + (t._count?.inscripciones ?? 0), 0),
    [talleresFilt],
  );

  // Planes anuales → campo de fecha: fechaInicio
  const planesFilt = useMemo(
    () => planes.filter((p) => enRango(p.fechaInicio, desde, hasta)),
    [planes, desde, hasta],
  );

  // Programas culturales → campo de fecha: fechaInicio
  const programasFilt = useMemo(
    () => programas.filter((p) => enRango(p.fechaInicio, desde, hasta)),
    [programas, desde, hasta],
  );

  // Promotores → campo de fecha: fechaRegistro
  const promotoresFilt = useMemo(
    () => promotores.filter((p) => enRango(p.fechaRegistro, desde, hasta)),
    [promotores, desde, hasta],
  );

  // Artistas / colaboradores: no tienen campo de fecha → NO se filtran por rango.
  const artistasFilt = artistas;

  // ─────────── Datos para gráficas (reflejan el filtro) ───────────
  const eventosPorTipo = useMemo(() => {
    const m = new Map<string, number>();
    eventosFilt.forEach((e) => m.set(e.tipoEvento || 'Otro', (m.get(e.tipoEvento || 'Otro') || 0) + 1));
    return Array.from(m, ([tipo, total]) => ({ tipo, total }));
  }, [eventosFilt]);

  const talleresPorTipo = useMemo(() => {
    const m = new Map<string, number>();
    talleresFilt.forEach((t) => m.set(t.tipo || 'Otro', (m.get(t.tipo || 'Otro') || 0) + 1));
    return Array.from(m, ([tipo, total]) => ({ tipo, total }));
  }, [talleresFilt]);

  // ─────────── Paneles resumen ───────────
  const eventosRecientes = useMemo(
    () => [...eventosFilt]
      .sort((a, b) => new Date(b.fechaEvento || 0).getTime() - new Date(a.fechaEvento || 0).getTime())
      .slice(0, 8),
    [eventosFilt],
  );
  const talleresProximos = useMemo(
    () => [...talleresFilt]
      .sort((a, b) => new Date(a.fechaTaller || 0).getTime() - new Date(b.fechaTaller || 0).getTime())
      .slice(0, 8),
    [talleresFilt],
  );

  // ─────────── Exportar resumen a Excel ───────────
  const exportarExcel = () => {
    const rows = [
      ['Métrica', 'Valor'],
      ['Eventos culturales', eventosFilt.length],
      ['  · Activos', eventosActivos],
      ['  · Cerrados', eventosCerrados],
      ['Talleres', talleresFilt.length],
      ['  · Abiertos', talleresAbiertos],
      ['Inscripciones (talleres)', inscripcionesTotal],
      ['Planes anuales', planesFilt.length],
      ['Programas culturales', programasFilt.length],
      ['Promotores', promotoresFilt.length],
      ['Artistas / colaboradores', artistasFilt.length],
      ['Periodo', hayFiltro ? `${desde ? fmtFecha(desde) : '…'} – ${hasta ? fmtFecha(hasta) : '…'}` : 'Todos'],
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Dashboard Cultura');
    XLSX.writeFile(wb, `Dashboard_Cultura_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // ─────────── Configuración de cada modal (drill-down) ───────────
  const modales: Record<ModalKey, { titulo: string; icon: IconType; head: string[]; filas: () => React.ReactNode[] }> = {
    eventos: {
      titulo: 'Eventos culturales', icon: FiCalendar,
      head: ['Título', 'Tipo', 'Comunidad', 'Fecha', 'Responsable', 'Estatus'],
      filas: () => eventosFilt.map((e) => (
        <Tr key={e.id}>
          <Td fontSize="xs"><Text noOfLines={1} maxW="220px">{e.titulo}</Text></Td>
          <Td fontSize="xs">{e.tipoEvento || '—'}</Td>
          <Td fontSize="xs">{e.comunidad || '—'}</Td>
          <Td fontSize="xs">{fmtFecha(e.fechaEvento)}</Td>
          <Td fontSize="xs">{e.responsable || '—'}</Td>
          <Td fontSize="xs"><Badge colorScheme={e.estatus === 'Cerrado' ? 'blue' : 'green'}>{e.estatus}</Badge></Td>
        </Tr>
      )),
    },
    talleres: {
      titulo: 'Talleres culturales', icon: FiBookOpen,
      head: ['Título', 'Tipo', 'Tallerista', 'Lugar', 'Fecha', 'Cupo', 'Inscritos', 'Estatus'],
      filas: () => talleresFilt.map((t) => (
        <Tr key={t.id}>
          <Td fontSize="xs"><Text noOfLines={1} maxW="200px">{t.titulo}</Text></Td>
          <Td fontSize="xs">{t.tipo || '—'}</Td>
          <Td fontSize="xs">{t.tallerista || '—'}</Td>
          <Td fontSize="xs">{t.lugar || '—'}</Td>
          <Td fontSize="xs">{fmtFecha(t.fechaTaller)}</Td>
          <Td fontSize="xs" isNumeric>{t.cupo ?? '—'}</Td>
          <Td fontSize="xs" isNumeric>{t._count?.inscripciones ?? 0}</Td>
          <Td fontSize="xs"><Badge colorScheme={t.estatus === 'Abierto' ? 'green' : t.estatus === 'Concluido' ? 'blue' : 'gray'}>{t.estatus}</Badge></Td>
        </Tr>
      )),
    },
    inscripciones: {
      titulo: 'Inscripciones por taller', icon: FiUsers,
      head: ['Taller', 'Tipo', 'Tallerista', 'Fecha', 'Cupo', 'Inscritos'],
      filas: () => talleresConInscritos.map((t) => (
        <Tr key={t.id}>
          <Td fontSize="xs"><Text noOfLines={1} maxW="220px">{t.titulo}</Text></Td>
          <Td fontSize="xs">{t.tipo || '—'}</Td>
          <Td fontSize="xs">{t.tallerista || '—'}</Td>
          <Td fontSize="xs">{fmtFecha(t.fechaTaller)}</Td>
          <Td fontSize="xs" isNumeric>{t.cupo ?? '—'}</Td>
          <Td fontSize="xs" isNumeric fontWeight="bold" color={RED}>{t._count?.inscripciones ?? 0}</Td>
        </Tr>
      )),
    },
    planes: {
      titulo: 'Planes anuales de cultura', icon: FiLayers,
      head: ['Año', 'Nombre del plan', 'Inicio', 'Fin', 'Responsable', '% Avance', 'Estatus'],
      filas: () => planesFilt.map((p) => (
        <Tr key={p.id}>
          <Td fontSize="xs">{p.anio}</Td>
          <Td fontSize="xs"><Text noOfLines={1} maxW="220px">{p.nombrePlan}</Text></Td>
          <Td fontSize="xs">{fmtFecha(p.fechaInicio)}</Td>
          <Td fontSize="xs">{fmtFecha(p.fechaFin)}</Td>
          <Td fontSize="xs">{p.responsable || '—'}</Td>
          <Td fontSize="xs" isNumeric>{p.porcentajeAvance || 0}%</Td>
          <Td fontSize="xs"><Badge colorScheme={p.estatus === 'Activo' ? 'green' : p.estatus === 'Concluido' ? 'blue' : 'gray'}>{p.estatus}</Badge></Td>
        </Tr>
      )),
    },
    programas: {
      titulo: 'Programas culturales', icon: FiGrid,
      head: ['Programa', 'Comunidad', 'Tipo', 'Responsable', 'Inicio', 'Presupuesto', 'Plan'],
      filas: () => programasFilt.map((p) => (
        <Tr key={p.id}>
          <Td fontSize="xs"><Text noOfLines={1} maxW="200px">{p.nombrePrograma}</Text></Td>
          <Td fontSize="xs">{p.comunidad || '—'}</Td>
          <Td fontSize="xs">{p.tipoProgramaCultural || '—'}</Td>
          <Td fontSize="xs">{p.responsable || '—'}</Td>
          <Td fontSize="xs">{fmtFecha(p.fechaInicio)}</Td>
          <Td fontSize="xs" isNumeric>{fmtPesos(p.presupuestoEstimado)}</Td>
          <Td fontSize="xs"><Text noOfLines={1} maxW="160px">{p._plan || '—'}</Text></Td>
        </Tr>
      )),
    },
    promotores: {
      titulo: 'Promotores culturales', icon: FiUser,
      head: ['Nombre', 'CURP', 'Giro', 'Especialidad', 'Teléfono', 'Registro'],
      filas: () => promotoresFilt.map((p) => (
        <Tr key={p.id}>
          <Td fontSize="xs">{p.nombre}</Td>
          <Td fontSize="xs">{p.curp || '—'}</Td>
          <Td fontSize="xs">{p.giro || '—'}</Td>
          <Td fontSize="xs">{p.especialidad || '—'}</Td>
          <Td fontSize="xs">{p.telefono || '—'}</Td>
          <Td fontSize="xs">{fmtFecha(p.fechaRegistro)}</Td>
        </Tr>
      )),
    },
    artistas: {
      titulo: 'Artistas / colaboradores', icon: FiMusic,
      head: ['Tipo', 'Nombre', 'Disciplina', 'Teléfono', 'Correo', 'Honorarios'],
      filas: () => artistasFilt.map((a) => (
        <Tr key={a.id}>
          <Td fontSize="xs">{a.tipo || '—'}</Td>
          <Td fontSize="xs">{a.nombre}</Td>
          <Td fontSize="xs">{a.disciplina || '—'}</Td>
          <Td fontSize="xs">{a.telefono || '—'}</Td>
          <Td fontSize="xs">{a.correo || '—'}</Td>
          <Td fontSize="xs" isNumeric>{fmtPesos(a.honorarios)}</Td>
        </Tr>
      )),
    },
  };

  // ─────────── Config de gráficas ───────────
  const barEventos = {
    labels: eventosPorTipo.map((e) => e.tipo),
    datasets: [{ label: 'Eventos', data: eventosPorTipo.map((e) => e.total), backgroundColor: COLORS, borderRadius: 4 }],
  };
  const doughnutTalleres = {
    labels: talleresPorTipo.map((t) => t.tipo),
    datasets: [{ data: talleresPorTipo.map((t) => t.total), backgroundColor: COLORS, borderWidth: 2 }],
  };
  const chartOptions = {
    responsive: true,
    plugins: { legend: { position: 'bottom' as const } },
    maintainAspectRatio: false,
  };

  if (cargando) {
    return (
      <VStack pt={79} py={12} spacing={3}>
        <Spinner size="xl" color={RED} thickness="3px" />
        <Text color="gray.500">Cargando indicadores...</Text>
      </VStack>
    );
  }

  const cfg = modal ? modales[modal] : null;

  return (
    <VStack spacing={5} align="stretch" px={{ base: 2, md: 4 }} pt={79} pb={4}>
      {/* ─── Acciones ─── */}
      <HStack justify="flex-end" flexWrap="wrap" gap={2}>
        <Button size="sm" leftIcon={<FiRefreshCw />} variant="outline" onClick={cargar}>Actualizar</Button>
        <Button size="sm" leftIcon={<FiDownload />} colorScheme="green" variant="outline" onClick={exportarExcel}>
          Exportar Excel
        </Button>
      </HStack>

      {/* ─── Filtro de fecha ─── */}
      <Box bg="white" borderRadius="lg" boxShadow="sm" p={4} borderTop={`3px solid ${RED}`}>
        <Wrap spacing={4} align="flex-end">
          <WrapItem>
            <HStack spacing={2} color={RED}>
              <Icon as={FiFilter} />
              <Text fontWeight="bold" fontSize="sm">Filtrar por fecha</Text>
            </HStack>
          </WrapItem>
          <WrapItem>
            <Box>
              <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={1}>Desde</Text>
              <Input type="date" size="sm" value={desdeInput} maxW="170px"
                onChange={(e) => setDesdeInput(e.target.value)} focusBorderColor={RED} />
            </Box>
          </WrapItem>
          <WrapItem>
            <Box>
              <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={1}>Hasta</Text>
              <Input type="date" size="sm" value={hastaInput} maxW="170px"
                onChange={(e) => setHastaInput(e.target.value)} focusBorderColor={RED} />
            </Box>
          </WrapItem>
          <WrapItem>
            <HStack spacing={2}>
              <Button size="sm" color="white" bg={RED} _hover={{ bg: RED_DARK }} leftIcon={<FiFilter />} onClick={aplicar}>
                Aplicar
              </Button>
              <Button size="sm" variant="outline" color={RED} borderColor={RED} _hover={{ bg: 'gray.50' }}
                leftIcon={<FiX />} onClick={limpiar} isDisabled={!hayFiltro && !desdeInput && !hastaInput}>
                Limpiar
              </Button>
            </HStack>
          </WrapItem>
          {hayFiltro && (
            <WrapItem>
              <Badge bg={RED} color="white" px={2} py={1} borderRadius="md" fontSize="2xs">
                Rango: {desde ? fmtFecha(desde) : '…'} – {hasta ? fmtFecha(hasta) : '…'}
              </Badge>
            </WrapItem>
          )}
        </Wrap>
      </Box>

      <Text fontSize="xs" color="gray.400">Da clic en cualquier indicador para ver el detalle de los registros.</Text>

      {/* ─── KPIs interactivos ─── */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
        <TarjetaKPI icon={FiCalendar} label="Eventos culturales" value={eventosFilt.length}
          help={`${eventosActivos} activos · ${eventosCerrados} cerrados`} color="#5b182b"
          onClick={() => setModal('eventos')} />
        <TarjetaKPI icon={FiBookOpen} label="Talleres" value={talleresFilt.length}
          help={`${talleresAbiertos} abiertos`} color="#2980B9" onClick={() => setModal('talleres')} />
        <TarjetaKPI icon={FiUsers} label="Inscripciones talleres" value={inscripcionesTotal}
          help={`${talleresConInscritos.length} talleres con inscritos`} color="#2471A3"
          onClick={() => setModal('inscripciones')} />
        <TarjetaKPI icon={FiLayers} label="Planes anuales" value={planesFilt.length}
          help="Planeación cultural" color="#8E44AD" onClick={() => setModal('planes')} />
        <TarjetaKPI icon={FiGrid} label="Programas culturales" value={programasFilt.length}
          help="Programas por comunidad" color="#C0392B" onClick={() => setModal('programas')} />
        <TarjetaKPI icon={FiUser} label="Promotores" value={promotoresFilt.length}
          help="Padrón de promotores" color="#16A085" onClick={() => setModal('promotores')} />
        <TarjetaKPI icon={FiMusic} label="Artistas / Colab." value={artistasFilt.length}
          help="Sin fecha · total del padrón" color="#D35400" onClick={() => setModal('artistas')} />
        <TarjetaKPI icon={FiTrendingUp} label="Registros del periodo"
          value={eventosFilt.length + talleresFilt.length + programasFilt.length}
          help="Eventos + talleres + programas" color="#27AE60"
          onClick={() => setModal('eventos')} />
      </SimpleGrid>

      {/* ─── Gráficas (reflejan el filtro) ─── */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
        <Box bg="white" borderRadius="lg" boxShadow="sm" p={4}>
          <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={3}>Eventos por tipo</Text>
          {eventosPorTipo.length > 0 ? (
            <Box h="220px"><Bar data={barEventos} options={chartOptions} /></Box>
          ) : (
            <Text color="gray.400" fontSize="sm" textAlign="center" py={8}>Sin datos</Text>
          )}
        </Box>
        <Box bg="white" borderRadius="lg" boxShadow="sm" p={4}>
          <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={3}>Talleres por tipo</Text>
          {talleresPorTipo.length > 0 ? (
            <Box h="220px"><Doughnut data={doughnutTalleres} options={chartOptions} /></Box>
          ) : (
            <Text color="gray.400" fontSize="sm" textAlign="center" py={8}>Sin datos</Text>
          )}
        </Box>
      </SimpleGrid>

      {/* ─── Paneles resumen ─── */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
        <Box bg="white" borderRadius="lg" boxShadow="sm" p={4}>
          <Text fontSize="sm" fontWeight="bold" color="#5b182b" mb={3}>Eventos recientes</Text>
          {eventosRecientes.length === 0 ? (
            <Text fontSize="sm" color="gray.400">Sin eventos en el periodo</Text>
          ) : (
            <VStack align="stretch" spacing={2}>
              {eventosRecientes.map((e) => (
                <HStack key={e.id} justify="space-between" p={2} borderRadius="md" _hover={{ bg: 'gray.50' }}>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" noOfLines={1}>{e.titulo}</Text>
                    <Text fontSize="xs" color="gray.500">{fmtFecha(e.fechaEvento)}</Text>
                  </Box>
                  <Badge colorScheme={e.estatus === 'Cerrado' ? 'blue' : 'green'} fontSize="xs">{e.estatus}</Badge>
                </HStack>
              ))}
            </VStack>
          )}
        </Box>

        <Box bg="white" borderRadius="lg" boxShadow="sm" p={4}>
          <Text fontSize="sm" fontWeight="bold" color="#2980B9" mb={3}>Talleres del periodo</Text>
          {talleresProximos.length === 0 ? (
            <Text fontSize="sm" color="gray.400">Sin talleres en el periodo</Text>
          ) : (
            <VStack align="stretch" spacing={2}>
              {talleresProximos.map((t) => (
                <HStack key={t.id} justify="space-between" p={2} borderRadius="md" _hover={{ bg: 'gray.50' }}>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" noOfLines={1}>{t.titulo}</Text>
                    <Text fontSize="xs" color="gray.500">{fmtFecha(t.fechaTaller)} · {t.lugar || '—'}</Text>
                  </Box>
                  <Badge colorScheme={t.estatus === 'Abierto' ? 'green' : t.estatus === 'Concluido' ? 'blue' : 'gray'} fontSize="xs">
                    {t.estatus}
                  </Badge>
                </HStack>
              ))}
            </VStack>
          )}
        </Box>
      </SimpleGrid>

      {/* ─── Modal drill-down ─── */}
      <Modal isOpen={!!cfg} onClose={() => setModal(null)} size="4xl" scrollBehavior="inside" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader bg={RED} color="white" borderTopRadius="md" py={3}>
            <HStack spacing={2}>
              {cfg && <Icon as={cfg.icon} />}
              <Text fontSize="md">{cfg?.titulo}</Text>
              {cfg && (
                <Badge bg="white" color={RED} borderRadius="full" px={2}>{cfg.filas().length}</Badge>
              )}
              {hayFiltro && (
                <Text fontSize="xs" opacity={0.85} fontWeight="normal">
                  ({desde ? fmtFecha(desde) : '…'} – {hasta ? fmtFecha(hasta) : '…'})
                </Text>
              )}
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody py={4}>
            {(() => {
              if (!cfg) return null;
              const filas = cfg.filas();
              if (filas.length === 0) {
                return (
                  <Center flexDirection="column" py={10} color="gray.400">
                    <Icon as={cfg.icon} boxSize={10} mb={2} />
                    <Text fontSize="sm">Sin registros{hayFiltro ? ' en el rango seleccionado' : ''}</Text>
                  </Center>
                );
              }
              return (
                <Box overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead bg={RED}>
                      <Tr>
                        {cfg.head.map((h) => (
                          <Th key={h} color="white" borderColor={RED}>{h}</Th>
                        ))}
                      </Tr>
                    </Thead>
                    <Tbody>{filas}</Tbody>
                  </Table>
                </Box>
              );
            })()}
          </ModalBody>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default DashboardCultura;
